import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Search, AlertCircle } from "lucide-react";

type ContactContext = {
  contact_id: string;
  status: string;
  display_name: string | null;
  signals: Record<string, unknown>;
  recent_interactions: Array<{
    event_id: string;
    type: string;
    occurred_at: string;
    summary: string;
  }>;
  what_worked: Record<string, unknown>;
  opt_outs: Record<string, unknown>;
  synopsis: string;
  generated_at: string;
};

type LookupTables = {
  contact: Record<string, unknown> | null;
  identifiers: Array<Record<string, unknown>>;
  events: Array<Record<string, unknown>>;
  projection: Record<string, unknown> | null;
};

const CustomerGraphDebug = () => {
  const { businessId } = useAuth();
  const [mode, setMode] = useState<"contact_id" | "channel">("contact_id");
  const [contactIdInput, setContactIdInput] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [channelUserId, setChannelUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<ContactContext | null>(null);
  const [tables, setTables] = useState<LookupTables | null>(null);

  const channelTypeFor = (c: string) =>
    c === "whatsapp" ? "whatsapp_e164"
    : c === "instagram" ? "instagram_psid"
    : c === "facebook_messenger" ? "facebook_psid"
    : c === "line" ? "line_user_id"
    : c === "telegram" ? "telegram_chat_id"
    : c === "email" ? "email"
    : "web_visitor_id";

  const fetchAll = async (resolvedContactId: string) => {
    if (!businessId) throw new Error("No business loaded — please refresh and try again");

    const { data: ctxData, error: ctxErr } = await supabase.functions.invoke(
      "customer-graph-context",
      { body: { contact_id: resolvedContactId, business_id: businessId } },
    );
    if (ctxErr) throw new Error(ctxErr.message ?? "context fetch failed");
    if ((ctxData as { error?: string })?.error) {
      throw new Error((ctxData as { error: string }).error);
    }

    const [contactRow, identifiers, events, projection] = await Promise.all([
      supabase.from("contacts").select("*").eq("contact_id", resolvedContactId).maybeSingle(),
      supabase.from("channel_identifiers").select("*").eq("contact_id", resolvedContactId),
      supabase
        .from("contact_events")
        .select("event_id, event_type, occurred_at, source_channel, source_actor, payload")
        .eq("contact_id", resolvedContactId)
        .order("occurred_at", { ascending: false })
        .limit(50),
      supabase.from("contact_projections").select("*").eq("contact_id", resolvedContactId).maybeSingle(),
    ]);

    return {
      context: ctxData as ContactContext,
      tables: {
        contact: contactRow.data ?? null,
        identifiers: identifiers.data ?? [],
        events: events.data ?? [],
        projection: projection.data ?? null,
      } as LookupTables,
    };
  };

  const onLookup = async () => {
    setError(null);
    setContext(null);
    setTables(null);
    setLoading(true);

    try {
      let resolvedContactId: string | null = null;

      if (mode === "contact_id") {
        if (!contactIdInput.trim()) throw new Error("Enter a contact_id");
        resolvedContactId = contactIdInput.trim();
      } else {
        if (!channelUserId.trim()) throw new Error("Enter a channel user id");
        const { data, error: lookupErr } = await supabase
          .from("channel_identifiers")
          .select("contact_id")
          .eq("channel_type", channelTypeFor(channel))
          .eq("identifier_value", channelUserId.trim())
          .maybeSingle();
        if (lookupErr) throw new Error(lookupErr.message);
        if (!data) throw new Error("No contact found for that channel/identifier");
        resolvedContactId = (data as { contact_id: string }).contact_id;
      }

      const { context: ctx, tables: tbl } = await fetchAll(resolvedContactId);
      setContext(ctx);
      setTables(tbl);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div className="flex gap-2">
          <Button
            variant={mode === "contact_id" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("contact_id")}
          >
            By contact_id
          </Button>
          <Button
            variant={mode === "channel" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("channel")}
          >
            By channel + identifier
          </Button>
        </div>

        {mode === "contact_id" ? (
          <Input
            placeholder="contact_id (uuid)"
            value={contactIdInput}
            onChange={(e) => setContactIdInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onLookup()}
          />
        ) : (
          <div className="flex gap-2">
            <select
              className="border rounded px-2 text-sm bg-background"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            >
              <option value="whatsapp">whatsapp</option>
              <option value="instagram">instagram</option>
              <option value="facebook_messenger">facebook_messenger</option>
              <option value="line">line</option>
              <option value="telegram">telegram</option>
              <option value="email">email</option>
              <option value="web">web</option>
            </select>
            <Input
              placeholder={
                channel === "whatsapp" ? "+15551234567 (E.164)"
                : channel === "instagram" ? "Instagram PSID"
                : channel === "facebook_messenger" ? "Facebook Page-scoped ID"
                : channel === "line" ? "LINE user ID"
                : channel === "telegram" ? "Telegram chat ID"
                : channel === "email" ? "person@example.com"
                : "web visitor id"
              }
              value={channelUserId}
              onChange={(e) => setChannelUserId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onLookup()}
            />
          </div>
        )}

        <Button onClick={onLookup} disabled={loading} className="w-full">
          {loading
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Search className="h-4 w-4 mr-2" />}
          Look up contact
        </Button>

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 p-3 rounded">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="break-words">{error}</span>
          </div>
        )}
      </Card>

      {context && tables && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-sm text-muted-foreground">contact_id</div>
              <code className="text-xs">{context.contact_id}</code>
            </div>
            <Badge variant="secondary">{context.status}</Badge>
          </div>
          <div className="bg-muted p-3 rounded text-sm mb-3 leading-relaxed">
            {context.synopsis}
          </div>

          <Tabs defaultValue="signals">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="signals">Signals</TabsTrigger>
              <TabsTrigger value="recent">Recent ({context.recent_interactions.length})</TabsTrigger>
              <TabsTrigger value="events">All events ({tables.events.length})</TabsTrigger>
              <TabsTrigger value="contact">Raw contact</TabsTrigger>
              <TabsTrigger value="identifiers">Identifiers ({tables.identifiers.length})</TabsTrigger>
              <TabsTrigger value="projection">Projection</TabsTrigger>
              <TabsTrigger value="opt_outs">Opt-outs</TabsTrigger>
              <TabsTrigger value="full">Full context</TabsTrigger>
            </TabsList>
            <TabsContent value="signals">
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                {JSON.stringify(context.signals, null, 2)}
              </pre>
            </TabsContent>
            <TabsContent value="recent">
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                {JSON.stringify(context.recent_interactions, null, 2)}
              </pre>
            </TabsContent>
            <TabsContent value="events">
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                {JSON.stringify(tables.events, null, 2)}
              </pre>
            </TabsContent>
            <TabsContent value="contact">
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                {JSON.stringify(tables.contact, null, 2)}
              </pre>
            </TabsContent>
            <TabsContent value="identifiers">
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                {JSON.stringify(tables.identifiers, null, 2)}
              </pre>
            </TabsContent>
            <TabsContent value="projection">
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                {JSON.stringify(tables.projection, null, 2)}
              </pre>
            </TabsContent>
            <TabsContent value="opt_outs">
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                {JSON.stringify(context.opt_outs, null, 2)}
              </pre>
            </TabsContent>
            <TabsContent value="full">
              <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                {JSON.stringify(context, null, 2)}
              </pre>
            </TabsContent>
          </Tabs>
        </Card>
      )}
    </div>
  );
};

export default CustomerGraphDebug;
